/**
 * IELTS / international-exam vocabulary quiz flow.
 * Pass mark and review delay match Backend articleRetention.service
 * (MASTERY_THRESHOLD = 70, STAGE_REPEAT_DAYS = 2).
 */

export const QUIZ_PASS_PERCENT = 70;
export const REVIEW_DELAY_DAYS = 2;
/** iPhone Safari cancels a fetch that starts in the same turn the article modal closes. */
export const QUIZ_FETCH_DEFER_MS = 400;
export const QUIZ_OPEN_GUARD_MS = 700;

export function isExamVocabularyArticle(article) {
  return !!(article && article.exam_type);
}

export function shouldShowVocabularyQuizButton(article) {
  return isExamVocabularyArticle(article) && article.id != null && article.id !== '';
}

export function quizLaunchPayload(article) {
  return {
    id: article.id,
    title: article.title || 'Article',
    isExam: true
  };
}

/**
 * Exam articles never auto-open the quiz. Educational readers keep the
 * existing post-close quiz (subscribed sequential track, or ≥90% scroll).
 */
export function shouldLaunchQuizOnClose({
  article,
  endSessionOk,
  completionPercentage,
  isEducationalSequential
}) {
  if (isExamVocabularyArticle(article)) return false;
  if (!article || article.id == null || article.id === '' || !endSessionOk) return false;
  return !!(isEducationalSequential || Number(completionPercentage) >= 90);
}

export function resolveReaderDismiss(intent, article, session = {}) {
  const base = {
    endSession: true,
    markPassed: false,
    launchQuiz: null
  };
  if (!article || article.id == null || article.id === '') return base;

  if (intent === 'start-quiz') {
    if (!shouldShowVocabularyQuizButton(article)) return base;
    return { ...base, launchQuiz: quizLaunchPayload(article) };
  }

  if (shouldLaunchQuizOnClose({
    article,
    endSessionOk: !!session.endSessionOk,
    completionPercentage: session.completionPercentage,
    isEducationalSequential: !!session.isEducationalSequential
  })) {
    return {
      ...base,
      launchQuiz: {
        id: article.id,
        title: article.title || 'Article',
        isExam: !!article.exam_type
      }
    };
  }

  return base;
}

export function scorePassesArticle(score) {
  return Number(score) >= QUIZ_PASS_PERCENT;
}

export function examQuizOutcome(score) {
  const passed = scorePassesArticle(score);
  return {
    passed,
    unlocksNext: passed,
    status: passed ? 'completed' : 'active',
    reviewDelayDays: passed ? null : REVIEW_DELAY_DAYS
  };
}

/**
 * Closing, skipping, a failed score, or a quiz that never starts does not
 * unlock the next article. An article that already passed stays passed.
 */
export function progressionAfterQuizAction(action, { score = null, previouslyPassed = false } = {}) {
  if (previouslyPassed) {
    return {
      passed: true,
      newlyUnlocksNext: false,
      preservesPassed: true,
      reviewDelayDays: null
    };
  }
  if (action === 'submit') {
    const outcome = examQuizOutcome(score);
    return {
      passed: outcome.passed,
      newlyUnlocksNext: outcome.unlocksNext,
      preservesPassed: false,
      reviewDelayDays: outcome.reviewDelayDays
    };
  }
  return {
    passed: false,
    newlyUnlocksNext: false,
    preservesPassed: true,
    reviewDelayDays: null
  };
}

export function retentionQuizMessageKey(data) {
  if (!data) return null;
  if (data.fetchError) return 'retentionQuizLoadError';
  if (data.alreadyCompleted) return 'retentionQuizAlreadyCompleted';
  if (!Array.isArray(data.questions) || data.questions.length === 0) return 'retentionQuizNoWords';
  return null;
}

export function createQuizSubmitGuard() {
  let locked = false;
  return {
    tryBegin() {
      if (locked) return false;
      locked = true;
      return true;
    },
    release() {
      locked = false;
    },
    isLocked() {
      return locked;
    }
  };
}
